import { AssetName } from "@moneta/core/browser";
import { useSearchParams } from "@remix-run/react";
import { useCallback, useMemo } from "react";
import { DevMode } from "~/entities";

type ContentView = "app" | "editor";

export const URL_STATE_PARAMS = ["mode", "path", "search", "view"] as const;

export const useAppUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentMode: DevMode = useMemo(() => {
    return searchParams.get("mode") === "experience" ? "experience" : "chat";
  }, [searchParams]);

  const currentPath = useMemo(() => {
    const path = searchParams.get("path") ?? "";
    const pathResult = AssetName.schema.safeParse(path);
    if (pathResult.success) {
      return pathResult.data;
    }
    return AssetName.schema.parse("/");
  }, [searchParams]);

  const currentSearch = useMemo(() => searchParams.get("search"), [searchParams]);

  const currentContentView = useMemo(() => searchParams.get("view") || "app", [searchParams]);

  const setAppUrlState = useCallback(
    (
      state: Partial<{
        mode?: DevMode;
        path?: AssetName;
        search?: string;
        view?: ContentView;
      }>,
      options?: {
        replace?: boolean;
      },
    ) => {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (state.mode) {
            newParams.set("mode", state.mode);
          }
          if (state.path) {
            newParams.set("path", state.path);
          }
          if (state.search) {
            newParams.set("search", state.search);
          }
          if (state.view) {
            newParams.set("view", state.view);
          }
          return newParams;
        },
        {
          preventScrollReset: true,
          ...options,
        },
      );
    },
    [setSearchParams],
  );

  return {
    currentMode,
    currentPath,
    currentSearch,
    currentContentView,
    setAppUrlState,
  };
};
